import { TODAY_SOFT_LIMIT, type MyWeekEntry } from '@/models/MyWeek'

/**
 * Splits the entries of a week into the blocks of the page.
 *
 * Everything comes from the backend: `state`, `today`, `today_date` and `done_date` compared with
 * the backend's `week.today` — the client never derives "today" from its own clock.
 *
 * Done rows stay where they were, struck through, until the day ends:
 * - a done entry whose `today_date` is the user's today was on "Heute" today → stays in "Heute";
 * - a done entry that became done today (`done_date`) stays in "Diese Woche";
 * - every other done entry goes to "Erledigt diese Woche".
 */
export interface MyWeekGroups {
  today: MyWeekEntry[]
  week: MyWeekEntry[]
  waiting: MyWeekEntry[]
  done: MyWeekEntry[]
  /** Open entries on "Heute" — what the soft limit counts. */
  todayOpen: number
  /** All done entries of the week, struck-through ones included. */
  doneCount: number
  /** Planned + done; waiting entries are not part of the progress. */
  progressTotal: number
}

export function partition(entries: MyWeekEntry[], todayDate: string): MyWeekGroups {
  const groups: MyWeekGroups = {
    today: [],
    week: [],
    waiting: [],
    done: [],
    todayOpen: 0,
    doneCount: 0,
    progressTotal: 0,
  }

  for (const entry of entries) {
    if (entry.state === 'waiting') {
      groups.waiting.push(entry)
      continue
    }
    groups.progressTotal++
    if (entry.state === 'done') {
      groups.doneCount++
      if (entry.today_date !== null && entry.today_date === todayDate) groups.today.push(entry)
      else if (entry.done_date !== null && entry.done_date === todayDate) groups.week.push(entry)
      else groups.done.push(entry)
      continue
    }
    if (entry.today) {
      groups.today.push(entry)
      groups.todayOpen++
    } else {
      groups.week.push(entry)
    }
  }

  return groups
}

/** More than the soft limit on "Heute" — the count turns orange and the hint warns. */
export function isTodayOverLimit(todayOpen: number): boolean {
  return todayOpen > TODAY_SOFT_LIMIT
}

/** Free places on "Heute" below the soft limit (0 when full or over). */
export function freeTodaySlots(todayOpen: number): number {
  return Math.max(0, TODAY_SOFT_LIMIT - todayOpen)
}

/** overdue > due today > customer replied > the rest. */
function morningScore(entry: MyWeekEntry, todayDate: string): number {
  if (entry.signal === 'overdue') return 3
  if (entry.task.due_date_local !== null && entry.task.due_date_local === todayDate) return 2
  if (entry.signal === 'customer_replied') return 1
  return 0
}

/**
 * What "Heute ist noch leer. Das bietet sich an:" offers: up to `limit` open rows of "Diese Woche",
 * overdue first, then due today, then customer replied, then the week's own order.
 */
export function morningPicks(weekRows: MyWeekEntry[], todayDate: string, limit = TODAY_SOFT_LIMIT): MyWeekEntry[] {
  return weekRows
    .filter((entry) => entry.state === 'planned')
    .map((entry, index) => ({ entry, index, score: morningScore(entry, todayDate) }))
    .sort((a, b) => b.score - a.score || a.index - b.index)
    .slice(0, limit)
    .map((item) => item.entry)
}

/**
 * The visible order of the movable rows (Heute, then Diese Woche) after moving one entry up or
 * down by one place. Crossing the border between the blocks moves the entry into the other block
 * (like the drag & drop does). Returns null when there is nowhere to go.
 */
export function moveByOne(
  today: string[],
  week: string[],
  id: string,
  direction: -1 | 1,
): { today_ids: string[]; week_ids: string[] } | null {
  const all = [...today, ...week]
  const index = all.indexOf(id)
  if (index < 0) return null
  const inToday = index < today.length
  const target = index + direction
  if (target < 0 || target >= all.length) return null

  // Up from the first week row: becomes the last row of Heute; down from the last Heute row:
  // becomes the first week row. The order of the whole list stays the same.
  if (inToday && direction === 1 && index === today.length - 1) {
    return { today_ids: today.slice(0, -1), week_ids: [id, ...week] }
  }
  if (!inToday && direction === -1 && index === today.length) {
    return { today_ids: [...today, id], week_ids: week.slice(1) }
  }

  const swap = (list: string[], from: number, to: number) => {
    const next = [...list]
    ;[next[from], next[to]] = [next[to], next[from]]
    return next
  }
  if (inToday) return { today_ids: swap(today, index, target), week_ids: [...week] }
  const offset = today.length
  return { today_ids: [...today], week_ids: swap(week, index - offset, target - offset) }
}
